// Smart Daara - Physical school: rooms
import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { physicalAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Trash2, DoorOpen } from 'lucide-react';
import { toast } from 'sonner';

export default function RoomsPage() {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [rooms, setRooms] = useState([]);
  const [form, setForm] = useState({ name: '', capacity: 40, room_type: 'classroom' });

  const typeLabels = {
    classroom: fr ? 'Salle de classe' : 'Classroom',
    lab: fr ? 'Laboratoire' : 'Lab',
    computer: fr ? 'Salle info' : 'Computer room',
    gym: fr ? 'Gymnase' : 'Gym',
  };

  const load = () => physicalAPI.listRooms().then((r) => setRooms(r.data));
  useEffect(() => { load(); }, []);

  const addRoom = async () => {
    if (!form.name.trim()) return;
    await physicalAPI.createRoom(form);
    setForm({ name: '', capacity: 40, room_type: 'classroom' });
    load();
    toast.success(fr ? 'Salle créée' : 'Room created');
  };

  return (
    <div className="space-y-6" data-testid="rooms-page">
      <h1 className="text-2xl font-outfit font-bold text-slate-900">{fr ? 'Salles' : 'Rooms'}</h1>
      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="text-base">{fr ? 'Ajouter une salle' : 'Add a room'}</CardTitle></CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-4 gap-3 items-end">
            <div><Label className="text-xs">{fr ? 'Nom' : 'Name'}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="room-name-input" /></div>
            <div><Label className="text-xs">{fr ? 'Capacité' : 'Capacity'}</Label><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 40 })} /></div>
            <div>
              <Label className="text-xs">Type</Label>
              <select className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm" value={form.room_type} onChange={(e) => setForm({ ...form, room_type: e.target.value })}>
                {Object.entries(typeLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <Button className="bg-emerald-700" onClick={addRoom} data-testid="add-room-btn">{fr ? 'Ajouter' : 'Add'}</Button>
          </div>
        </CardContent>
      </Card>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rooms.map((r) => (
          <Card key={r.id} className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <DoorOpen className="w-5 h-5 text-emerald-700" />
                <div>
                  <p className="font-medium">{r.name}</p>
                  <p className="text-xs text-slate-500">{typeLabels[r.room_type]} · {r.capacity} {fr ? 'places' : 'seats'}</p>
                </div>
              </div>
              <button onClick={async () => { await physicalAPI.deleteRoom(r.id); load(); }} className="text-red-400 hover:text-red-600" data-testid={`delete-room-${r.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </CardContent>
          </Card>
        ))}
        {rooms.length === 0 && <p className="text-slate-400 text-sm col-span-3 text-center py-6">{fr ? 'Aucune salle' : 'No rooms'}</p>}
      </div>
    </div>
  );
}
